import { ApiReportTemplatePayorTypeChoices } from "@api/graphql/types-and-hooks";

export interface PayorCopy {
  displayName: string;
  submissionDialog: {
    initialTitle: string;
    resubmitTitle: string;
    initialDescription: string;
    resubmitDescription: string;
    initialFooter: string;
    resubmitFooter: string;
    initialError: string;
    resubmitError: string;
  };
  submittingDialog: {
    title: string;
  };
  button: {
    submit: string;
    resubmit: string;
  };
  tooltips: {
    approved: string;
    awaitingReview: string;
    needsRevision: string;
    submitting: string;
  };
  notifications: {
    submitted: string;
    submitFailed: string;
  };
  errorContexts: {
    pdfGenerationFailed: string;
    submissionFailed: string;
  };
}

// Add a payor here to opt it into the in-app submission flow. Membership in
// this map is what `hasPayorSubmissionIntegration` checks.
const payorConfig: Partial<Record<ApiReportTemplatePayorTypeChoices, PayorCopy>> = {
  [ApiReportTemplatePayorTypeChoices.Catalight]: {
    displayName: "Catalight",
    submissionDialog: {
      initialTitle: "Submit Report to Catalight",
      resubmitTitle: "Resubmit Report to Catalight",
      initialDescription:
        "Once submitted, this report will be sent to Catalight for review. Their team will evaluate the assessment and may return feedback for you to address. You'll be notified when a response is ready.",
      resubmitDescription:
        "Once resubmitted, your updated report will be sent back to Catalight for another review. Their team will re-evaluate the assessment and may return additional feedback. You'll be notified when a response is ready.",
      initialFooter: "By submitting, you're confirming this report is accurate and ready for payor review.",
      resubmitFooter: "By resubmitting, you're confirming this report is accurate and ready for payor review.",
      initialError: "Submission failed.",
      resubmitError: "Resubmission failed.",
    },
    submittingDialog: { title: "Submitting to Catalight" },
    button: { submit: "Submit to Catalight", resubmit: "Resubmit to Catalight" },
    tooltips: {
      approved: "Report has been approved by Catalight.",
      awaitingReview: "Report has been submitted and is awaiting Catalight review.",
      needsRevision: "Report needs revision. Please update and resubmit to Catalight.",
      submitting: "Report is being submitted to Catalight.",
    },
    notifications: {
      submitted: "Report submitted to Catalight.",
      submitFailed: "Failed to submit report to Catalight.",
    },
    errorContexts: {
      pdfGenerationFailed: "Failed to generate PDF for Catalight submission",
      submissionFailed: "Failed to submit report to Catalight",
    },
  },
};

export const getPayorConfig = (
  payorType: ApiReportTemplatePayorTypeChoices | null | undefined,
): PayorCopy | undefined => (payorType ? payorConfig[payorType] : undefined);

export const hasPayorSubmissionIntegration = (
  payorType: ApiReportTemplatePayorTypeChoices | null | undefined,
): boolean => getPayorConfig(payorType) !== undefined;

// Human-readable label per payor enum. Matches the wording used in the
// admin template Payor Type dropdown so QA-package surfaces stay consistent.
const payorDisplayNames: Record<ApiReportTemplatePayorTypeChoices, string> = {
  [ApiReportTemplatePayorTypeChoices.Aetna]: "Aetna",
  [ApiReportTemplatePayorTypeChoices.Anthem]: "Anthem",
  [ApiReportTemplatePayorTypeChoices.Bcbs]: "BCBS",
  [ApiReportTemplatePayorTypeChoices.BeaconHealthOptions]: "Beacon Health Options",
  [ApiReportTemplatePayorTypeChoices.Bsw]: "BSW",
  [ApiReportTemplatePayorTypeChoices.Catalight]: "Catalight",
  [ApiReportTemplatePayorTypeChoices.Cigna]: "Cigna",
  [ApiReportTemplatePayorTypeChoices.EmiHealth]: "EMI Health",
  [ApiReportTemplatePayorTypeChoices.Evernorth]: "Evernorth",
  [ApiReportTemplatePayorTypeChoices.Humana]: "Humana",
  [ApiReportTemplatePayorTypeChoices.KaiserPermanente]: "Kaiser Permanente",
  [ApiReportTemplatePayorTypeChoices.MagellanHealth]: "Magellan Health",
  [ApiReportTemplatePayorTypeChoices.Medicaid]: "Medicaid",
  [ApiReportTemplatePayorTypeChoices.Molina]: "Molina",
  [ApiReportTemplatePayorTypeChoices.Optum]: "Optum",
  [ApiReportTemplatePayorTypeChoices.Tricare]: "Tricare",
  [ApiReportTemplatePayorTypeChoices.UnitedHealthcare]: "United Healthcare",
};

// Accepts the enum or the raw backend string (e.g. "TRICARE") since QA
// package payor_types arrive as unwrapped strings from the org-facing query.
export const getPayorDisplayName = (payor: string): string => {
  const enumHit = payorDisplayNames[payor as ApiReportTemplatePayorTypeChoices];
  if (enumHit) return enumHit;
  // Fallback for unknown/legacy values: title-case the enum key.
  return payor
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
